import {getAllAssetPropertyDocumentTypes} from '@sollapay/domain/trust-types';
import {DocumentContext, DocumentEntityType} from '@sollapay/enums';
import {useEffect, useMemo} from 'react';
import {useForm, useWatch} from 'react-hook-form';

import {useDocumentRequiredForm} from '@/features/documents';
import {useUpsertAssetMutation} from '@/features/trust-accounts/activation/hooks';
import {useTrustTypeStrategy} from '@/features/trust-accounts/providers';
import {useFormShell} from '@/hooks';

import {flattenFieldErrors} from '../../../utils';
import {AssetDetailsRequiredFieldsSchema} from '../../../validation';
import {
  acceptsAssetField,
  EMPTY_ASSET_DETAILS_FORM_VALUES,
  toAssetDetailsFormValues,
  toUpsertTrustAssetPayload
} from '../utils';

import type {AssetDetailsFormValues} from '../utils';
import type {FormStateSnapshot} from '@/hooks';
import type {AssetType} from '@sollapay/enums';
import type {AssetDTO} from '@sollapay/types';

interface UseAssetDetailsFormParams {
  trustAccountId: string;
  asset: AssetDTO | null | undefined;
  isLoading: boolean;
  isError: boolean;
  isReadOnly: boolean;
  onFormStateChange?: (state: FormStateSnapshot) => void;
}

export const useAssetDetailsForm = ({
  trustAccountId,
  asset,
  isLoading,
  isError,
  isReadOnly,
  onFormStateChange
}: UseAssetDetailsFormParams) => {
  const upsertAssetMutation = useUpsertAssetMutation(trustAccountId);
  const strategy = useTrustTypeStrategy();

  const form = useForm<AssetDetailsFormValues>({
    defaultValues: EMPTY_ASSET_DETAILS_FORM_VALUES
  });

  // `strategy` is a required dep, not just `asset` — an unsaved account (asset still null) that
  // switches transaction type must re-derive its default asset type from the new strategy.
  const assetDefaults = useMemo(
    () => toAssetDetailsFormValues(asset, strategy.getDefaultAssetType()),
    [asset, strategy]
  );

  useEffect(() => {
    form.reset(assetDefaults);
  }, [form, assetDefaults]);

  // A live "still missing" hint, same as every other activation step.
  const allValues = useWatch({
    control: form.control,
    defaultValue: EMPTY_ASSET_DETAILS_FORM_VALUES
  });
  const validationResult = AssetDetailsRequiredFieldsSchema.safeParse(allValues);
  const hasFieldErrors = !validationResult.success;
  const fieldErrors = flattenFieldErrors(validationResult.success ? null : validationResult.error);

  // The agreement(s) this deal type offers are exactly what AI extracts from — a second-hand
  // account never sees an option agreement, and vice versa. Returns a fresh array per call, so
  // memoize it — downstream hooks (AI extraction, the missing-document check) key off its identity.
  const propertyDocumentTypes = useMemo(() => strategy.getAssetPropertyDocumentTypes(), [strategy]);
  // Offered alongside the required ones but never gated on — e.g. an option assignment's
  // underlying sale agreement. No caller needs its identity stable, so it's left unmemoized.
  const optionalPropertyDocumentTypes = strategy.getOptionalAssetPropertyDocumentTypes();
  const allPropertyDocumentTypes = useMemo(
    () => getAllAssetPropertyDocumentTypes(strategy),
    [strategy]
  );

  // No `onFormStateChange` here — its own hasErrors folds into the single combined value below,
  // rather than racing useFormShell's own onFormStateChange call for the same step.
  const {missingDocumentTypes} = useDocumentRequiredForm({
    entityType: DocumentEntityType.TRUST_ACCOUNT,
    entityId: trustAccountId,
    documentContext: DocumentContext.TRUST_DETAILS,
    requiredDocumentTypes: propertyDocumentTypes
  });
  const hasErrors = hasFieldErrors || missingDocumentTypes.size > 0;

  const handleSave = form.handleSubmit(async values => {
    // Unlike every other field here, assetType has no nullable/omittable form on the wire (a
    // native Postgres enum), so there's no draft to send without it — its own always-visible
    // "required" hint already tells the lawyer why nothing happened.
    if (!values.assetType) return;

    const result = await upsertAssetMutation.mutateAsync(toUpsertTrustAssetPayload(values));
    form.reset(toAssetDetailsFormValues(result.data.asset));
  });

  const handleCancel = () => {
    form.reset(assetDefaults);
    upsertAssetMutation.reset();
  };

  const allowedAssetTypes = strategy.getAllowedAssetTypes();

  // One asset per trust account, so the default single unscoped document scope applies, and the
  // schema's target fields match this form's own field names 1:1.
  const {aiFields, isProcessing, suggestionsCount, markFieldModified, stopAllScopes} =
    useFormShell<AssetDetailsFormValues>({
      isDirty: form.formState.isDirty,
      isLoading,
      isSaving: upsertAssetMutation.isPending,
      isError,
      isSaveError: upsertAssetMutation.isError,
      hasErrors,
      isReadOnly,
      onSave: handleSave,
      onCancel: handleCancel,
      onFormStateChange,
      ai: {
        entityType: DocumentEntityType.TRUST_ACCOUNT,
        entityId: trustAccountId,
        documentContext: DocumentContext.TRUST_DETAILS,
        documentTypes: allPropertyDocumentTypes,
        getValues: form.getValues,
        setValue: form.setValue,
        // null drops a suggestion for a field the selected asset type doesn't have, so AI never
        // fills one this step doesn't render — which the server would then reject on save.
        resolveField: targetField =>
          acceptsAssetField(form.getValues('assetType'), targetField) ? targetField : null,
        // The extractor doesn't know trust type, so a disallowed extracted assetType is dropped here.
        toFieldValue: (field, extractedValue) =>
          field === 'assetType' && !allowedAssetTypes.includes(extractedValue as AssetType)
            ? ''
            : extractedValue
      }
    });

  // Save/cancel/dirty state reaches the surrounding modal through `onFormStateChange`, so only
  // what the step itself renders is returned here.
  return {
    form,
    handleSave,
    saveError: upsertAssetMutation.error,
    fieldErrors,
    aiFields,
    isProcessing,
    suggestionsCount,
    markFieldModified,
    stopAllScopes,
    allowedAssetTypes,
    propertyDocumentTypes,
    optionalPropertyDocumentTypes,
    missingDocumentTypes
  };
};
